<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import useAutomationValues from 'dashboard/composables/useAutomationValues';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { FLOW_ACTION_TYPES } from '../../constants';
import { getActionIcon } from 'dashboard/helper/automationHelper';
import { provideDropdownTeleport } from 'dashboard/components-next/dropdown-menu/base/provider';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update']);

const CONTACT_EMAIL_TOKEN = '{{contact.email}}';

const { t } = useI18n();
const store = useStore();
const { getActionDropdownValues } = useAutomationValues();

provideDropdownTeleport();

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
});

const actions = computed(() => props.data.actions || []);

const nodeLabel = computed({
  get() {
    return props.data.label || '';
  },
  set(value) {
    emit('update', { label: value });
  },
});

const actionTypeOptions = computed(() =>
  FLOW_ACTION_TYPES.map(a => ({
    id: a.key,
    name: t(`AUTOMATION.ACTIONS.${a.label}`),
    icon: getActionIcon(a.key),
  }))
);

const emitActions = newActions => {
  emit('update', { actions: newActions });
};

const addAction = () => {
  emitActions([...actions.value, { action_name: '', action_params: [] }]);
};

const removeAction = index => {
  emitActions(actions.value.filter((_, i) => i !== index));
};

const updateAction = (index, patch) => {
  const updated = actions.value.map((a, i) =>
    i === index ? { ...a, ...patch } : a
  );
  emitActions(updated);
};

const getActionType = actionName =>
  FLOW_ACTION_TYPES.find(a => a.key === actionName);

const getInputType = actionName => getActionType(actionName)?.inputType;

const getDropdownValues = actionName =>
  getActionDropdownValues(actionName) || [];

const getPlaceholderKey = actionName => {
  switch (actionName) {
    case 'send_message':
      return 'FLOW_BUILDER.NODE_EDITOR.ACTION.MESSAGE_PLACEHOLDER';
    case 'add_private_note':
      return 'FLOW_BUILDER.NODE_EDITOR.ACTION.NOTE_PLACEHOLDER';
    case 'wait_delay':
      return 'FLOW_BUILDER.NODE_EDITOR.ACTION.WAIT_SECONDS_PLACEHOLDER';
    case 'send_webhook_event':
      return 'FLOW_BUILDER.NODE_EDITOR.ACTION.WEBHOOK_PLACEHOLDER';
    case 'send_email_transcript':
      return 'FLOW_BUILDER.NODE_EDITOR.ACTION.EMAIL_PLACEHOLDER';
    default:
      return '';
  }
};

const insertContactEmail = index => {
  const action = actions.value[index];
  const params = action?.action_params || [];
  const current = Array.isArray(params) ? params[0] || '' : params || '';
  const existing = current
    .split(',')
    .map(email => email.trim())
    .filter(Boolean);
  if (existing.some(email => email.replace(/\s+/g, '') === CONTACT_EMAIL_TOKEN))
    return;
  updateAction(index, {
    action_params: [[...existing, CONTACT_EMAIL_TOKEN].join(',')],
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1.5">
      <label class="text-[11px] font-medium text-n-slate-11">
        {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.NODE_NAME') }}
      </label>
      <NextInput
        v-model="nodeLabel"
        type="text"
        size="sm"
        :placeholder="t('FLOW_BUILDER.SIDEBAR.ACTION')"
      />
    </div>

    <div
      v-for="(action, index) in actions"
      :key="index"
      class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak dark:border-n-strong"
    >
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="
            action.action_name
              ? {
                  id: action.action_name,
                  name:
                    t(
                      `AUTOMATION.ACTIONS.${
                        FLOW_ACTION_TYPES.find(
                          a => a.key === action.action_name
                        )?.label
                      }`
                    ) || action.action_name,
                }
              : null
          "
          :options="actionTypeOptions"
          :placeholder="t('FLOW_BUILDER.NODE_EDITOR.ACTION.SELECT_ACTION')"
          placeholder-icon="i-lucide-settings"
          disable-deselect
          class="flex-1 min-w-0"
          @update:model-value="
            value => {
              updateAction(index, {
                action_name: value?.id || '',
                action_params: [],
              });
            }
          "
        />
        <SingleSelect
          v-if="
            action.action_name &&
            getInputType(action.action_name) === 'search_select'
          "
          :model-value="
            (() => {
              const params = action.action_params || [];
              if (!Array.isArray(params) || params.length === 0) return null;
              const id = params[0];
              const match = getDropdownValues(action.action_name).find(
                v => v.id === id
              );
              return match || { id, name: String(id) };
            })()
          "
          :options="getDropdownValues(action.action_name)"
          class="flex-1 min-w-0"
          @update:model-value="
            value => {
              updateAction(index, {
                action_params: value ? [value.id] : [],
              });
            }
          "
        />
        <MultiSelect
          v-else-if="
            action.action_name &&
            getInputType(action.action_name) === 'multi_select'
          "
          :model-value="
            (() => {
              const params = action.action_params || [];
              if (!Array.isArray(params) || params.length === 0) return [];
              return params
                .map(id => {
                  const match = getDropdownValues(action.action_name).find(
                    v => v.id === id
                  );
                  return match || { id, name: String(id) };
                })
                .filter(Boolean);
            })()
          "
          :options="getDropdownValues(action.action_name)"
          class="flex-1 min-w-0"
          @update:model-value="
            value => {
              updateAction(index, {
                action_params: Array.isArray(value) ? value.map(v => v.id) : [],
              });
            }
          "
        />
        <button
          class="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-n-ruby-11 hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.NODE_EDITOR.ACTION.REMOVE_ACTION')"
          @click="removeAction(index)"
        >
          <span class="i-lucide-trash text-sm" />
        </button>
      </div>

      <template
        v-if="
          action.action_name &&
          getInputType(action.action_name) &&
          !['search_select', 'multi_select'].includes(
            getInputType(action.action_name)
          )
        "
      >
        <div
          class="flex flex-col gap-1.5 pt-2 border-t border-n-slate-2 dark:border-n-slate-3"
        >
          <label class="text-[11px] font-medium text-n-slate-11">
            {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.PARAMS') }}
          </label>

          <div
            v-if="getInputType(action.action_name) === 'email'"
            class="flex items-center gap-2"
          >
            <NextInput
              :model-value="
                Array.isArray(action.action_params)
                  ? action.action_params[0] || ''
                  : action.action_params || ''
              "
              type="text"
              size="sm"
              class="flex-1"
              :placeholder="t(getPlaceholderKey(action.action_name))"
              @update:model-value="
                value => updateAction(index, { action_params: [value] })
              "
            />
            <button
              class="px-3 py-1.5 text-xs font-medium rounded-lg bg-n-blue-1 dark:bg-n-blue-2 text-n-blue-text hover:bg-n-blue-2 dark:hover:bg-n-blue-3 whitespace-nowrap transition-colors"
              @click="insertContactEmail(index)"
            >
              {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.INSERT_CONTACT_EMAIL') }}
            </button>
          </div>

          <NextInput
            v-else-if="getInputType(action.action_name) === 'url'"
            :model-value="
              Array.isArray(action.action_params)
                ? action.action_params[0] || ''
                : action.action_params || ''
            "
            type="url"
            size="sm"
            :placeholder="t(getPlaceholderKey(action.action_name))"
            @update:model-value="
              value => updateAction(index, { action_params: [value] })
            "
          />

          <input
            v-else-if="getInputType(action.action_name) === 'number'"
            :value="
              Array.isArray(action.action_params)
                ? action.action_params[0] || 1
                : action.action_params || 1
            "
            type="number"
            min="1"
            max="3600"
            class="w-full px-3 py-2 text-sm border border-n-weak dark:border-n-strong rounded-lg bg-transparent text-n-slate-12 outline-none focus:border-n-blue-5"
            :placeholder="t(getPlaceholderKey(action.action_name))"
            @input="
              updateAction(index, {
                action_params: [parseInt($event.target.value, 10) || 1],
              })
            "
          />

          <WootMessageEditor
            v-else-if="getInputType(action.action_name) === 'textarea'"
            :model-value="
              Array.isArray(action.action_params)
                ? action.action_params[0] || ''
                : action.action_params || ''
            "
            rows="4"
            enable-variables
            :placeholder="t(getPlaceholderKey(action.action_name))"
            class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
            @update:model-value="
              value => updateAction(index, { action_params: [value] })
            "
          />
        </div>
      </template>

      <div
        v-else-if="action.action_name && !getInputType(action.action_name)"
        class="pt-2 border-t border-n-slate-2 dark:border-n-slate-3"
      >
        <p class="text-[11px] text-n-slate-11 italic">
          {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.NO_PARAMS') }}
        </p>
      </div>
    </div>

    <button
      class="flex items-center justify-center gap-1.5 py-2.5 px-3 text-xs font-medium rounded-lg border border-dashed border-n-weak dark:border-n-strong text-n-slate-11 hover:border-n-blue-5 hover:text-n-blue-5 transition-colors"
      @click="addAction"
    >
      <span class="i-lucide-plus text-sm" />
      {{ t('FLOW_BUILDER.NODE_EDITOR.ACTION.ADD_ACTION') }}
    </button>
  </div>
</template>
